import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createChart, ColorType, CandlestickSeries, HistogramSeries, LineSeries } from 'lightweight-charts';
import { getBinanceSymbol, requiresMockData } from '../../constants/markets';

const CandleChart = ({ symbol = 'btcusdt', interval = '5m', onPriceUpdate }) => {
    const { t } = useTranslation();
    const chartContainerRef = useRef();
    const chartRef = useRef();
    const candleSeriesRef = useRef();
    const volumeSeriesRef = useRef();
    const ma5SeriesRef = useRef();
    const ma10SeriesRef = useRef();
    const ma20SeriesRef = useRef();
    const wsRef = useRef();
    const currentDataRef = useRef([]); // Store full data
    const currentVolumeDataRef = useRef([]); // Store full volume data for MA calc

    // Optimization: Use ref for legend values to avoid React re-renders on every mouse move
    const legendRef = useRef(null);

    useEffect(() => {
        if (!chartContainerRef.current) return;

        let isMounted = true;
        let crosshairSubscription = null;

        // Initialize Chart
        const chart = createChart(chartContainerRef.current, {
            layout: {
                background: { type: ColorType.Solid, color: 'transparent' },
                textColor: '#848e9c',
                attributionLogo: false,
            },
            grid: {
                vertLines: { color: 'rgba(255, 255, 255, 0.02)' },
                horzLines: { color: 'rgba(255, 255, 255, 0.02)' },
            },
            rightPriceScale: {
                borderColor: 'rgba(255, 255, 255, 0.05)',
                visible: true,
            },
            timeScale: {
                barSpacing: 10,
                minBarSpacing: 5,
                borderColor: 'rgba(255, 255, 255, 0.05)',
                timeVisible: true,
                secondsVisible: false,
            },
            crosshair: {
                mode: 1,
                vertLine: {
                    color: 'rgba(212, 175, 55, 0.2)',
                    labelBackgroundColor: '#1e222d',
                },
                horzLine: {
                    color: 'rgba(212, 175, 55, 0.2)',
                    labelBackgroundColor: '#1e222d',
                },
            },
        });

        const candleSeries = chart.addSeries(CandlestickSeries, {
            upColor: '#0ecb81',
            downColor: '#f6465d',
            borderVisible: false,
            wickUpColor: '#0ecb81',
            wickDownColor: '#f6465d',
        });

        const volumeSeries = chart.addSeries(HistogramSeries, {
            color: '#26a69a',
            priceFormat: { type: 'volume' },
            priceScaleId: '', // overlay
        });

        volumeSeries.priceScale().applyOptions({
            scaleMargins: { top: 0.8, bottom: 0 },
        });

        // Add MA Lines
        const ma5Series = chart.addSeries(LineSeries, {
            color: '#fcd535', // MA5 Yellow
            lineWidth: 1,
            priceLineVisible: false,
            crosshairMarkerVisible: false,
        });
        const ma10Series = chart.addSeries(LineSeries, {
            color: '#a035fc', // MA10 Purple
            lineWidth: 1,
            priceLineVisible: false,
            crosshairMarkerVisible: false,
        });
        const ma20Series = chart.addSeries(LineSeries, {
            color: '#3498db', // MA20 Blue
            lineWidth: 1,
            priceLineVisible: false,
            crosshairMarkerVisible: false,
        });

        chartRef.current = chart;
        candleSeriesRef.current = candleSeries;
        volumeSeriesRef.current = volumeSeries;
        ma5SeriesRef.current = ma5Series;
        ma10SeriesRef.current = ma10Series;
        ma20SeriesRef.current = ma20Series;

        // Manual Resize Handling
        const handleResize = () => {
            if (chartContainerRef.current && chartRef.current) {
                chartRef.current.applyOptions({
                    width: chartContainerRef.current.clientWidth,
                    height: chartContainerRef.current.clientHeight
                });
            }
        };
        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(chartContainerRef.current);

        // Helper to format number with commas
        const formatNum = (num, decimals = 2) => {
            if (num === undefined || num === null || isNaN(num)) return '-';
            return parseFloat(num).toLocaleString('en-US', {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals
            });
        };

        // Helper to format volume
        const formatVol = (val) => {
            if (val === undefined || val === null || isNaN(val)) return '-';
            if (val >= 1000000) return (val / 1000000).toFixed(3) + 'M';
            if (val >= 1000) return (val / 1000).toFixed(3) + 'K';
            return val.toFixed(2);
        };

        // Helper to update Legend DOM directly
        const updateLegend = (data) => {
            if (legendRef.current) {
                const { time, open, high, low, close, volume, volMa5, volMa10, volMa20 } = data;
                legendRef.current.innerHTML = `
                    <div style="font-family: 'Inter', sans-serif; font-size: 11px; line-height: 1.6; display: flex; flex-direction: column; gap: 4px;">
                        <div style="display: flex; gap: 15px; color: #848e9c;">
                            <span>Time: <span style="color: #b7bdc6">${time}</span></span>
                            <span>Open: <span style="color: ${open >= close ? '#f6465d' : '#0ecb81'}">${formatNum(open)}</span></span>
                            <span>High: <span style="color: ${open >= close ? '#f6465d' : '#0ecb81'}">${formatNum(high)}</span></span>
                            <span>Low: <span style="color: ${open >= close ? '#f6465d' : '#0ecb81'}">${formatNum(low)}</span></span>
                            <span>Close: <span style="color: ${open >= close ? '#f6465d' : '#0ecb81'}">${formatNum(close)}</span></span>
                            <span>Volume: <span style="color: #b7bdc6">${formatVol(volume)}</span></span>
                        </div>
                        <div style="display: flex; gap: 15px; align-items: center;">
                            <span style="color: #848e9c; opacity: 0.7;">VOL(5,10,20)</span>
                            <span style="color: #fcd535">MA5: ${formatVol(volMa5)}</span>
                            <span style="color: #a035fc">MA10: ${formatVol(volMa10)}</span>
                            <span style="color: #3498db">MA20: ${formatVol(volMa20)}</span>
                            <span style="color: #0ecb81">VOLUME: ${formatVol(volume)}</span>
                        </div>
                    </div>
                `;
            }
        };

        // Legend dynamics
        crosshairSubscription = (param) => {
            if (!isMounted || !param.time || param.point === undefined || !param.seriesData.get(candleSeries)) {
                return;
            }
            const data = param.seriesData.get(candleSeries);
            const volData = param.seriesData.get(volumeSeries);

            // Calculate MA on the fly using the ref data
            const selectedTime = param.time;
            const volDataArray = currentVolumeDataRef.current;
            const index = volDataArray.findIndex(d => d.time === selectedTime);

            let ma5, ma10, ma20;
            if (index !== -1) {
                const getMA = (idx, count) => {
                    if (idx < count - 1) return undefined;
                    let sum = 0;
                    for (let j = 0; j < count; j++) sum += volDataArray[idx - j].value;
                    return sum / count;
                };
                ma5 = getMA(index, 5);
                ma10 = getMA(index, 10);
                ma20 = getMA(index, 20);
            }

            updateLegend({
                time: new Date(param.time * 1000).toLocaleString('en-GB', { hour12: false }),
                open: data.open,
                high: data.high,
                low: data.low,
                close: data.close,
                volume: volData ? volData.value : 0,
                volMa5: ma5,
                volMa10: ma10,
                volMa20: ma20
            });
        };
        chart.subscribeCrosshairMove(crosshairSubscription);

        // Helper to calculate MA
        const calculateMA = (data, count, field = 'close') => {
            if (!data || data.length < count) return [];
            const ma = [];
            for (let i = 0; i < data.length; i++) {
                if (i < count - 1) {
                    ma.push({ time: data[i].time, value: undefined });
                    continue;
                }
                const sum = data.slice(i - count + 1, i + 1).reduce((acc, d) => acc + d[field], 0);
                ma.push({ time: data[i].time, value: sum / count });
            }
            return ma.filter(d => d.value !== undefined);
        };

        // Mock Data Generator
        const generateMockData = (basePrice, count = 500) => {
            const data = [];
            let time = Math.floor(Date.now() / 1000) - (count * 60 * (interval === '1h' ? 60 : interval === '1d' ? 1440 : 5));
            let price = basePrice;

            for (let i = 0; i < count; i++) {
                const volatility = price * 0.002; // 0.2% volatility
                const change = (Math.random() - 0.5) * volatility;
                const open = price;
                const close = price + change;
                const high = Math.max(open, close) + Math.random() * volatility * 0.5;
                const low = Math.min(open, close) - Math.random() * volatility * 0.5;

                data.push({
                    time: time,
                    open: parseFloat(open.toFixed(2)),
                    high: parseFloat(high.toFixed(2)),
                    low: parseFloat(low.toFixed(2)),
                    close: parseFloat(close.toFixed(2))
                });

                price = close;
                time += 60 * (interval === '1h' ? 60 : interval === '1d' ? 1440 : 5);
            }
            return data;
        };

        // Fetch Historical Data
        const fetchHistory = async () => {
            try {
                // Use centralized symbol mapping
                const apiSymbol = getBinanceSymbol(symbol || 'BTCUSDT');
                const isMock = requiresMockData(symbol);

                let formattedData = [];
                let formattedVolume = [];

                if (isMock) {
                    const basePrice = 4900; // Current gold price circa 2026
                    const mockKlines = generateMockData(basePrice);
                    formattedData = mockKlines;
                    formattedVolume = mockKlines.map(d => ({
                        time: d.time,
                        value: Math.random() * 1000,
                        color: d.close >= d.open ? 'rgba(14, 203, 129, 0.3)' : 'rgba(246, 70, 93, 0.3)'
                    }));
                } else {
                    const response = await fetch(`https://api.binance.com/api/v3/klines?symbol=${apiSymbol}&interval=${interval}&limit=500`);
                    if (!response.ok) throw new Error('Network response was not ok');
                    const data = await response.json();

                    if (!isMounted) return;

                    formattedData = data.map(d => ({
                        time: d[0] / 1000,
                        open: parseFloat(d[1]),
                        high: parseFloat(d[2]),
                        low: parseFloat(d[3]),
                        close: parseFloat(d[4]),
                    }));
                    formattedVolume = data.map(d => ({
                        time: d[0] / 1000,
                        value: parseFloat(d[5]),
                        color: parseFloat(d[4]) >= parseFloat(d[1]) ? 'rgba(14, 203, 129, 0.3)' : 'rgba(246, 70, 93, 0.3)',
                    }));
                }

                // Store for local access
                currentDataRef.current = formattedData;
                currentVolumeDataRef.current = formattedVolume;

                candleSeries.setData(formattedData);
                volumeSeries.setData(formattedVolume);
                ma5Series.setData(calculateMA(formattedData, 5));
                ma10Series.setData(calculateMA(formattedData, 10));
                ma20Series.setData(calculateMA(formattedData, 20));

                // Set initial legend
                if (formattedData.length > 0) {
                    const lastIdx = formattedData.length - 1;
                    const lastVolData = formattedVolume[lastIdx];

                    const getMA = (idx, count) => {
                        if (idx < count - 1) return undefined;
                        let sum = 0;
                        for (let j = 0; j < count; j++) sum += formattedVolume[idx - j].value;
                        return sum / count;
                    };

                    updateLegend({
                        time: new Date(formattedData[lastIdx].time * 1000).toLocaleString('en-GB', { hour12: false }),
                        open: formattedData[lastIdx].open,
                        high: formattedData[lastIdx].high,
                        low: formattedData[lastIdx].low,
                        close: formattedData[lastIdx].close,
                        volume: lastVolData ? lastVolData.value : 0,
                        volMa5: getMA(lastIdx, 5),
                        volMa10: getMA(lastIdx, 10),
                        volMa20: getMA(lastIdx, 20)
                    });

                    if (onPriceUpdate) {
                        const change24h = formattedData[0].open > 0
                            ? (((formattedData[lastIdx].close - formattedData[0].open) / formattedData[0].open) * 100).toFixed(2)
                            : '0.00';
                        onPriceUpdate({
                            price: formattedData[lastIdx].close.toFixed(2),
                            high: formattedData[lastIdx].high.toFixed(2),
                            low: formattedData[lastIdx].low.toFixed(2),
                            open: formattedData[lastIdx].open.toFixed(2),
                            volume: formatVol(lastVolData ? lastVolData.value : 0),
                            change24h: change24h
                        });
                    }
                }

                // If mock, start simulated live updates
                if (isMock) {
                    const simulationInterval = setInterval(() => {
                        if (!isMounted) return;

                        const currentLast = formattedData[formattedData.length - 1];

                        // More subtle movement for mock data
                        const volatility = currentLast.close * 0.0001;
                        const change = (Math.random() - 0.5) * volatility;

                        const newClose = currentLast.close + change;
                        const newHigh = Math.max(currentLast.high, newClose);
                        const newLow = Math.min(currentLast.low, newClose);

                        currentLast.close = parseFloat(newClose.toFixed(2));
                        currentLast.high = parseFloat(newHigh.toFixed(2));
                        currentLast.low = parseFloat(newLow.toFixed(2));

                        const updatedCandle = { ...currentLast };
                        candleSeries.update(updatedCandle);

                        // Get volume and MAs for legend
                        const lastIdx = formattedData.length - 1;
                        const lastVol = currentVolumeDataRef.current[lastIdx];

                        const getMA = (idx, count) => {
                            if (idx < count - 1) return undefined;
                            let sum = 0;
                            // Using ref to be safe across closure updates
                            const volData = currentVolumeDataRef.current;
                            for (let j = 0; j < count; j++) sum += volData[idx - j].value;
                            return sum / count;
                        };

                        updateLegend({
                            time: new Date(updatedCandle.time * 1000).toLocaleString('en-GB', { hour12: false }),
                            open: updatedCandle.open.toFixed(2),
                            high: updatedCandle.high.toFixed(2),
                            low: updatedCandle.low.toFixed(2),
                            close: updatedCandle.close.toFixed(2),
                            volume: lastVol ? lastVol.value : 0,
                            volMa5: getMA(lastIdx, 5),
                            volMa10: getMA(lastIdx, 10),
                            volMa20: getMA(lastIdx, 20)
                        });

                        if (onPriceUpdate) {
                            const change24h = formattedData[0].open > 0
                                ? (((updatedCandle.close - formattedData[0].open) / formattedData[0].open) * 100).toFixed(2)
                                : '0.00';
                            onPriceUpdate({
                                price: updatedCandle.close.toFixed(2),
                                high: updatedCandle.high.toFixed(2),
                                low: updatedCandle.low.toFixed(2),
                                open: updatedCandle.open.toFixed(2),
                                volume: formatVol(lastVol ? lastVol.value : 0),
                                change24h: change24h
                            });
                        }
                    }, 2000); // Update every 2 seconds for a smoother feel

                    return () => clearInterval(simulationInterval);
                }

            } catch (error) {
                if (isMounted) {
                    console.error('Error fetching history:', error);
                    if (legendRef.current) {
                        legendRef.current.innerHTML = `<div style="color: #f6465d">Error: ${error.message}</div>`;
                    }
                }
            }
        };

        const simulationCleanup = fetchHistory();

        // WebSocket for Real-time (skip only for symbols with no Binance equivalent)
        let ws = null;
        let wsTimeout = null;
        const currentSymbol = symbol || '';
        // Skip WebSocket only for symbols that require mock data (like EURO pairs)
        if (currentSymbol && !requiresMockData(currentSymbol)) {
            // Delay WebSocket connection to survive React 18 Strict Mode
            wsTimeout = setTimeout(() => {
                if (wsRef.current) return; // Already connected from a previous render

                // Use centralized Binance symbol mapping
                const wsSymbol = getBinanceSymbol(currentSymbol).toLowerCase();
                const streamName = `${wsSymbol}@kline_${interval}`;
                const wsUrl = `wss://stream.binance.com:9443/ws/${streamName}`;
                ws = new WebSocket(wsUrl);

                ws.onmessage = (event) => {
                    if (!isMounted) return;
                    try {
                        const message = JSON.parse(event.data);
                        const kline = message.k;
                        if (!kline) return;

                        const candle = {
                            time: kline.t / 1000,
                            open: parseFloat(kline.o),
                            high: parseFloat(kline.h),
                            low: parseFloat(kline.l),
                            close: parseFloat(kline.c),
                        };
                        const volume = {
                            time: kline.t / 1000,
                            value: parseFloat(kline.v),
                            color: parseFloat(kline.c) >= parseFloat(kline.o) ? 'rgba(14, 203, 129, 0.3)' : 'rgba(246, 70, 93, 0.3)',
                        };

                        candleSeries.update(candle);
                        volumeSeries.update(volume);

                        // Re-calculate and update MA lines for the last candle (simplified)
                        // In a production app, we would append and recalculate more efficiently
                        const allData = candleSeries.data ? [...candleSeries.data, candle] : [candle];
                        const ma5val = allData.length >= 5 ? allData.slice(-5).reduce((a, b) => a + b.close, 0) / 5 : undefined;
                        const ma10val = allData.length >= 10 ? allData.slice(-10).reduce((a, b) => a + b.close, 0) / 10 : undefined;
                        const ma20val = allData.length >= 20 ? allData.slice(-20).reduce((a, b) => a + b.close, 0) / 20 : undefined;

                        if (ma5val) ma5Series.update({ time: candle.time, value: ma5val });
                        if (ma10val) ma10Series.update({ time: candle.time, value: ma10val });
                        if (ma20val) ma20Series.update({ time: candle.time, value: ma20val });

                        // Volume MAs for legend update (simplified on-the-fly)
                        // We can't easily access all history here without making volumeSeries.data persistent
                        // So we refresh legend with what we have

                        if (onPriceUpdate) {
                            const change24h = candle.open > 0
                                ? (((candle.close - candle.open) / candle.open) * 100).toFixed(2)
                                : '0.00';
                            onPriceUpdate({
                                price: candle.close.toFixed(2),
                                high: candle.high.toFixed(2),
                                low: candle.low.toFixed(2),
                                open: candle.open.toFixed(2),
                                volume: formatVol(volume.value),
                                change24h: change24h
                            });
                        }
                    } catch (e) { }
                };
                wsRef.current = ws;
            }, 100); // 100ms delay to survive strict mode
        }

        return () => {
            isMounted = false;
            if (wsTimeout) clearTimeout(wsTimeout);
            resizeObserver.disconnect();

            Promise.resolve(simulationCleanup).then(cleanup => {
                if (typeof cleanup === 'function') cleanup();
            }).catch(() => { }); // Ignore errors during cleanup

            if (chartRef.current) {
                if (crosshairSubscription) {
                    chartRef.current.unsubscribeCrosshairMove(crosshairSubscription);
                }
                chartRef.current.remove();
                chartRef.current = null;
            }

            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
        };
    }, [symbol, interval, onPriceUpdate]);

    return (
        <div style={{ position: 'relative', width: '100%', height: '400px' }}>
            <div
                ref={legendRef}
                className="chart-legend"
                style={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                    zIndex: 10,
                    fontSize: '11px',
                    color: '#848e9c',
                    pointerEvents: 'none',
                    background: 'rgba(11, 17, 24, 0.4)',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    lineHeight: '1.6'
                }}
            >
                <div className="legend-row">Loading indicators...</div>
            </div>
            <style>{`
                .chart-legend .legend-row {
                    display: flex;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .chart-legend .val {
                    color: #d1d4dc;
                    font-weight: bold;
                }
                .chart-legend .indicator-data {
                    margin-top: 4px;
                }
            `}</style>
            <div ref={chartContainerRef} style={{ width: '100%', height: '100%' }} />
        </div>
    );
};

export default CandleChart;
